import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X, ImageIcon, CheckCircle, AlertTriangle, Building2 } from 'lucide-react';
import UtilityCheckboxes from '@/components/UtilityCheckboxes';
import PropertyAttributesForm from '@/components/PropertyAttributesForm';
import MapAddressPicker from '@/components/MapAddressPicker';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitPropertyPage() {
  const { t, language } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [mainPhotoPreview, setMainPhotoPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [listingLimit, setListingLimit] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityMode, setCityMode] = useState('preset'); // 'preset' | 'other'
  const [form, setForm] = useState({
    title: '', description: '', price: '', area: '', land_size: '', property_type: 'apartment',
    operation_type: 'sale', state: '', city: '', neighborhood: '', address: '',
    latitude: 0, longitude: 0,
    bedrooms: 0, bathrooms: 0, main_photo: '', gallery: [],
    water_supply: [], electricity: [], connectivity: [],
    condition_status: '', condition_state: '', floor_level: '', features: [],
    reference: '', instagram_urls: ['', '', '']
  });

  const headers = { Authorization: `Bearer ${token}` };
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: typeof e === 'string' ? e : e.target.value }));

  useEffect(() => {
    axios.get(`${API}/users/listing-limit`, { headers })
      .then(r => setListingLimit(r.data))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/locations/states`)
      .then(r => setStates(r.data.states || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.state) { setCities([]); return; }
    axios.get(`${API}/locations/cities`, { params: { state: form.state } })
      .then(r => setCities(r.data.cities || []))
      .catch(() => setCities([]));
  }, [form.state]);

  const onStateChange = (val) => {
    setForm(f => ({ ...f, state: val, city: '' }));
    setCityMode('preset');
  };

  const onCitySelect = (val) => {
    if (val === '__other__') {
      setCityMode('other');
      setForm(f => ({ ...f, city: '' }));
    } else {
      setCityMode('preset');
      setForm(f => ({ ...f, city: val }));
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
    return res.data.path;
  };

  const handleMainPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMainPhotoPreview(URL.createObjectURL(file));
    try {
      const path = await uploadImage(file);
      setForm(f => ({ ...f, main_photo: path }));
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); setMainPhotoPreview(null); }
  };

  const handleGallery = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const preview = URL.createObjectURL(file);
      setGalleryPreviews(p => [...p, preview]);
      try {
        const path = await uploadImage(file);
        setForm(f => ({ ...f, gallery: [...f.gallery, path] }));
      } catch { toast.error('Upload failed'); }
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryPreviews(p => p.filter((_, i) => i !== index));
    setForm(f => ({ ...f, gallery: f.gallery.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || !form.state || !form.city || !form.property_type) {
      toast.error(language === 'es' ? 'Por favor selecciona Estado y Ciudad' : 'Please select State and City');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/properties`, {
        ...form, price: parseFloat(form.price), area: parseFloat(form.area || 0), land_size: parseFloat(form.land_size || 0),
        bedrooms: parseInt(form.bedrooms), bathrooms: parseInt(form.bathrooms),
        water_supply: form.water_supply, electricity: form.electricity, connectivity: form.connectivity,
        instagram_urls: (form.instagram_urls || []).map(u => (u || '').trim()).filter(Boolean).slice(0, 3),
      }, { headers });
      toast.success(t('submit.success'));
      // Fire feedback triggers based on the agent's total properties count
      if (user?.id) {
        const key = `waka-props-created-${user.id}`;
        const n = (parseInt(localStorage.getItem(key) || '0', 10) || 0) + 1;
        localStorage.setItem(key, String(n));
        setTimeout(() => {
          try {
            if (n === 1) window.__wakaFeedback?.fire?.('first_property_created');
            else if (n === 5) window.__wakaFeedback?.fire?.('5th_property_created');
          } catch {}
        }, 1500);
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="submit-property-page">
      {/* Property seekers (role=user) can't publish — show role-switch CTA */}
      {user?.role === 'user' && (
        <div className="border-2 border-[#D4AF37]/40 bg-gradient-to-br from-amber-50 to-white rounded-xl p-6 mb-6 text-center space-y-3" data-testid="submit-seeker-cta">
          <div className="w-12 h-12 mx-auto bg-[#D4AF37] rounded-2xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-[#0B192C]" />
          </div>
          <h2 className="font-heading text-xl text-[#0B192C]">{language === 'es' ? 'Tu cuenta está en modo Comprador' : 'Your account is in Buyer mode'}</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            {language === 'es'
              ? 'Para publicar propiedades necesitas convertirte en Agente. Es gratis y mantienes tu cuenta y favoritos.'
              : 'To publish properties you need to convert to an Agent. Free and keeps your account and favorites.'}
          </p>
          <a href="/become-agent" className="inline-block bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold px-6 py-2.5 rounded-lg text-sm" data-testid="submit-seeker-become-agent-btn">
            {language === 'es' ? 'Convertirme en Agente →' : 'Become an Agent →'}
          </a>
        </div>
      )}
      {user?.role !== 'user' && (
      <>
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">{t('submit.title')}</h1>
        <p className="text-slate-500 text-sm mt-1">{t('submit.subtitle')}</p>
      </div>

      {/* Listing Limit Banner */}
      {listingLimit && listingLimit.limit !== null && (
        <div className={`rounded-xl p-4 mb-6 flex items-center gap-3 ${listingLimit.remaining <= 0 ? 'bg-red-50 border-2 border-red-200' : 'bg-slate-50 border border-border'}`} data-testid="listing-limit-banner">
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${listingLimit.remaining <= 0 ? 'text-red-500' : 'text-slate-500'}`} />
          <div className="flex-1">
            <p className={`text-sm font-medium ${listingLimit.remaining <= 0 ? 'text-red-800' : 'text-slate-700'}`}>
              {listingLimit.remaining <= 0
                ? (language === 'es' ? `Has alcanzado el limite de ${listingLimit.limit} publicaciones activas.` : `You've reached your limit of ${listingLimit.limit} active listings.`)
                : (language === 'es' ? `${listingLimit.active} de ${listingLimit.limit} publicaciones activas usadas. Te quedan ${listingLimit.remaining}.` : `${listingLimit.active} of ${listingLimit.limit} active listings used. ${listingLimit.remaining} remaining.`)}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{t('submit.basicInfo')}</h3>
            <div>
              <Label>{t('submit.titleField')} *</Label>
              <Input value={form.title} onChange={set('title')} required className="mt-1 h-11 bg-slate-50" data-testid="submit-title" />
            </div>
            <div>
              <Label>{t('submit.descriptionField')} *</Label>
              <Textarea value={form.description} onChange={set('description')} required rows={4} className="mt-1 bg-slate-50" data-testid="submit-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('submit.priceField')} *</Label>
                <Input type="number" value={form.price} onChange={set('price')} required className="mt-1 h-11 bg-slate-50" data-testid="submit-price" />
              </div>
              <div>
                <Label>{t('submit.areaField')}</Label>
                <Input type="number" value={form.area} onChange={set('area')} className="mt-1 h-11 bg-slate-50" data-testid="submit-area" />
              </div>
              <div>
                <Label>{language === 'es' ? 'Terreno (m2)' : 'Land Size (m2)'}</Label>
                <Input type="number" value={form.land_size} onChange={set('land_size')} className="mt-1 h-11 bg-slate-50" data-testid="submit-land-size" placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('search.propertyType')} *</Label>
                <Select value={form.property_type} onValueChange={set('property_type')}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid="submit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">{t('types.apartment')}</SelectItem>
                    <SelectItem value="house">{t('types.house')}</SelectItem>
                    <SelectItem value="land">{t('types.land')}</SelectItem>
                    <SelectItem value="warehouse">{t('types.warehouse')}</SelectItem>
                    <SelectItem value="office">{t('types.office')}</SelectItem>
                    <SelectItem value="commercial_unit">{t('types.commercial_unit')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('search.operationType')}</Label>
                <Select value={form.operation_type} onValueChange={set('operation_type')}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid="submit-operation"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">{t('operations.sale')}</SelectItem>
                    <SelectItem value="rent">{t('operations.rent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{language === 'es' ? 'Referencia (código de la agencia)' : 'Reference (Agency Code)'}</Label>
              <Input
                value={form.reference}
                onChange={set('reference')}
                placeholder={language === 'es' ? 'Ej. MLS-1234, REF-789' : 'e.g. MLS-1234, REF-789'}
                className="mt-1 h-11 bg-slate-50"
                data-testid="submit-reference"
                maxLength={64}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {language === 'es'
                  ? 'Tu propio código interno. El WakaCode oficial se generará automáticamente al guardar.'
                  : "Your own internal code. The official WakaCode will be auto-generated on save."}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Property Attributes (condition, features, floor level, beds/baths dropdowns) */}
        {form.property_type !== 'land' && (
          <PropertyAttributesForm form={form} setForm={setForm} idPrefix="submit-attr" />
        )}

        {/* Location */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{t('submit.locationInfo')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{language === 'es' ? 'Estado' : 'State'} *</Label>
                <Select value={form.state} onValueChange={onStateChange}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50" data-testid="submit-state">
                    <SelectValue placeholder={language === 'es' ? 'Selecciona un Estado' : 'Select a State'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {states.map(s => (
                      <SelectItem key={s.key} value={s.key} data-testid={`submit-state-opt-${s.key}`}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('submit.cityField')} *</Label>
                <Select value={cityMode === 'other' ? '__other__' : form.city} onValueChange={onCitySelect} disabled={!form.state}>
                  <SelectTrigger className="mt-1 h-11 bg-slate-50 disabled:opacity-60" data-testid="submit-city">
                    <SelectValue placeholder={
                      !form.state
                        ? (language === 'es' ? 'Primero selecciona Estado' : 'Select state first')
                        : (language === 'es' ? 'Selecciona una Ciudad' : 'Select a City')
                    } />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {cities.map(c => (
                      <SelectItem key={c.key} value={c.name} data-testid={`submit-city-opt-${c.key}`}>{c.name}</SelectItem>
                    ))}
                    <SelectItem value="__other__" data-testid="submit-city-opt-other">{language === 'es' ? 'Otra…' : 'Other…'}</SelectItem>
                  </SelectContent>
                </Select>
                {cityMode === 'other' && (
                  <Input
                    value={form.city}
                    onChange={set('city')}
                    placeholder={language === 'es' ? 'Escribe el nombre de la ciudad' : 'Enter city name'}
                    className="mt-2 h-11 bg-slate-50"
                    data-testid="submit-city-other-input"
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('submit.neighborhoodField')}</Label>
                <Input value={form.neighborhood} onChange={set('neighborhood')} className="mt-1 h-11 bg-slate-50" data-testid="submit-neighborhood" />
              </div>
              <div>
                <Label>{t('submit.addressField')}</Label>
                <Input value={form.address} onChange={set('address')} className="mt-1 h-11 bg-slate-50" data-testid="submit-address" />
              </div>
            </div>

            {/* Interactive Map Picker — set the precise pin */}
            <div className="pt-2">
              <Label className="flex items-center justify-between gap-2 mb-1.5">
                <span>{language === 'es' ? 'Ubicación en el Mapa' : 'Pin on Map'}</span>
                <span className="text-[11px] font-normal text-slate-500">
                  {language === 'es' ? 'Opcional · mejora la precisión' : 'Optional · improves accuracy'}
                </span>
              </Label>
              <MapAddressPicker
                latitude={Number(form.latitude || 0)}
                longitude={Number(form.longitude || 0)}
                city={form.city}
                defaultQuery={[
                  form.address,
                  form.neighborhood,
                  form.city,
                  states.find(s => s.key === form.state)?.name || '',
                  'Venezuela',
                ].filter(Boolean).join(', ')}
                onChange={({ lat, lng, address }) => {
                  setForm(f => ({
                    ...f,
                    latitude: lat || 0,
                    longitude: lng || 0,
                    // Only auto-fill the address line if it's empty.
                    address: (!f.address && address) ? address : f.address,
                  }));
                }}
                testId="submit-map"
              />
            </div>
          </CardContent>
        </Card>

        {/* Utilities & Services */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{language === 'es' ? 'Servicios e Infraestructura' : 'Utilities & Services'}</h3>
            <div>
              <Label className="text-xs text-slate-600">{language === 'es' ? 'Suministro de Agua' : 'Water Supply'}</Label>
              <UtilityCheckboxes category="water_supply" selected={form.water_supply} onChange={v => setForm(f => ({ ...f, water_supply: v }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">{language === 'es' ? 'Electricidad' : 'Electricity'}</Label>
              <UtilityCheckboxes category="electricity" selected={form.electricity} onChange={v => setForm(f => ({ ...f, electricity: v }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">{language === 'es' ? 'Conectividad' : 'Connectivity'}</Label>
              <UtilityCheckboxes category="connectivity" selected={form.connectivity} onChange={v => setForm(f => ({ ...f, connectivity: v }))} />
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-heading font-semibold text-slate-700">{t('submit.mediaInfo')}</h3>
            {/* Main Photo */}
            <div>
              <Label>{t('submit.mainPhoto')}</Label>
              <div className="mt-2">
                {mainPhotoPreview ? (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden">
                    <img src={mainPhotoPreview} alt="Main" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => { setMainPhotoPreview(null); setForm(f => ({...f, main_photo: ''})); }}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                      <X className="w-3.5 h-3.5" />
                    </button>
                    {form.main_photo && <CheckCircle className="absolute bottom-2 right-2 w-5 h-5 text-green-500" />}
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-primary/50 transition-colors" data-testid="upload-main-photo">
                    <Upload className="w-6 h-6 text-slate-400 mb-1" />
                    <span className="text-sm text-slate-500">{t('submit.uploadPhoto')}</span>
                    <input type="file" accept="image/*" onChange={handleMainPhoto} className="hidden" />
                  </label>
                )}
              </div>
            </div>
            {/* Gallery */}
            <div>
              <Label>{t('submit.galleryPhotos')}</Label>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-3">
                {galleryPreviews.map((preview, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={preview} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeGalleryImage(i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-primary/50 transition-colors" data-testid="upload-gallery">
                  <ImageIcon className="w-5 h-5 text-slate-400" />
                  <span className="text-xs text-slate-500 mt-1">Add</span>
                  <input type="file" accept="image/*" multiple onChange={handleGallery} className="hidden" />
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instagram URLs (optional, max 3) */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base font-heading text-[#0B192C]">
                  {language === 'es' ? 'Instagram (opcional)' : 'Instagram (optional)'}
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {language === 'es'
                    ? 'Pega hasta 3 URLs de tus posts o reels para mostrarlos en la propiedad.'
                    : 'Paste up to 3 URLs of your posts or reels to show on this listing.'}
                </p>
              </div>
            </div>
            <div className="space-y-2" data-testid="submit-instagram-urls">
              {[0, 1, 2].map((i) => (
                <input
                  key={i}
                  type="url"
                  placeholder="https://www.instagram.com/p/ABC123/"
                  value={form.instagram_urls?.[i] || ''}
                  onChange={(e) => {
                    const next = [...(form.instagram_urls || ['', '', ''])];
                    next[i] = e.target.value;
                    setForm((f) => ({ ...f, instagram_urls: next }));
                  }}
                  className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:border-[#D4AF37]"
                  data-testid={`submit-instagram-url-${i}`}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] rounded-xl font-semibold text-base" data-testid="submit-property-btn">
          {submitting ? t('submit.submitting') : t('submit.submitBtn')}
        </Button>
      </form>
      </>
      )}
    </div>
  );
}
